import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { LabelAndValue } from '../../../../global/components/data-display/label-value';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { getEmployeesList } from '../../../../global/ducks/benadmin-api';
import { getFormattedDate } from '../../../../global/helpers/date-helpers';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { RoleObject } from '../../../../global/models/authenticated-model';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ArcRoutes } from '../../constants/routes';
import { CompensationProps, EmploymentProps } from '../../models/arc-epi-props';
import { OneTimePaymentView } from '../compensation/compensation-history/one-time-payment-view';
import { CalculatedTotalsDisplay } from '../compensation/shared/calculated-totals';
import { Position } from '../employment/employment-model';
import { setCurrentEmployee } from '../employment/employment-slice';
import { EmploymentCompensation } from './employment-compensation-models';

interface Props {
    settings: EmploymentProps;
    compensationSettings: CompensationProps;
    data: EmploymentCompensation;
    labelSettings: LabelSettingProps;
    isMycpg?: boolean; 
    isLatestEmployent: boolean;       
}

export function EmploymentCompensationItem(props: Props) {
    const { settings, compensationSettings, data, labelSettings, isMycpg, isLatestEmployent } = props;
	const scheduledPayment = data?.compensations?.compensations?.length > 0 && !isMycpg ? data?.compensations?.compensations[0] : null;
	const { yesText, noText } = labelSettings;
	const [accordionExpanded, setAccordionExpanded] = useState(false);
	const history = useHistory();
	const employmentPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Employment}`);
	const compensationPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Compensation}`);
    const compensationHistoryPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Compensation}${ArcRoutes.History}`);
    const roles = useSelector<TcsmStates, RoleObject>(state => state?.authenticatedState?.roleObject);
    const hasEditPermission = roles?.employee_roster?.some(role => role?.role_target === data?.employment?.employer_client_number);
    const isEditable = !data?.employment?.termination_date && hasEditPermission && isLatestEmployent;
	const dispatch = useDispatch();
	const buttonClasses = buttonStyles();


	const positionView = (position: Position) => {
		return (
			<>
				<p>{position?.position_title}</p>
				<p className='grey-caption'>{getFormattedDate(position?.start_date)} - {getFormattedDate(position?.end_date) || settings?.presentLabel}</p>
			</>
		);
	};

	const onClick = (redirect: string) => {
		dispatch(
			getEmployeesList(
				data?.employment?.employer_guid,
				(response) => {
					const match = response?.data?.filter(a => a?.employee?.guid === data?.employment?.employee_guid)[0];
					if (match) {
						dispatch(setCurrentEmployee(match?.employee));
					}
					history.push(redirect);
				}),
		);
	};

	function OnAccordionChanged(event: React.ChangeEvent<{}>, expanded: boolean) {
		setAccordionExpanded(expanded);
	}

	function EmploymentTitleDetails(isSummary: boolean) {
		if ((isSummary && accordionExpanded) ||
			!isSummary && !accordionExpanded) return null;

		return (<ContentSpacer>
			<h3 className='generic-accordion-item-subheader generic-accordion-item-subheader-expanded'>{settings?.employmentSectionTitle}</h3>
			<LabelAndValue label={settings?.institutionLabel} value={data?.employment?.employer_name} />
			{(!isMycpg && hasEditPermission) && <LabelAndValue label={settings?.scheduledHourLabel} value={data?.employment?.current_scheduled_hours} />}
		</ContentSpacer>);
	}

    function RendereNotAvailableText(notAvailableText: string){
        if (isEditable || !notAvailableText || !isLatestEmployent) return null;

        return (<WysiwygWithReactLink content={notAvailableText} />);
    }

    return (

        <Accordion
            onChange={ OnAccordionChanged }
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`content-${data?.employment?.id}`}
                id={`header-${data?.employment?.id}`}
            ><div>                            
                <h2>{getFormattedDate(data?.employment?.hire_date)} - {getFormattedDate(data?.employment?.termination_date) || settings?.presentLabel}</h2>                                   
                { EmploymentTitleDetails(true) }                     
             </div>
            </AccordionSummary>
            <AccordionDetails>
                { EmploymentTitleDetails(false) }                     
                <div>
                    <LabelAndValue
                        label={settings?.positionLabel}
                        value={
                            <>
                                {
                                    data?.employment?.position_titles?.map(p => {
                                        return positionView(p);
                                    })
                                }
                            </>
                        }
                    />
                    <LabelAndValue label={settings?.dioceseLabel} value={data?.employment?.employer_name} />
                    <LabelAndValue label={settings?.addressLabel} value={
                        <>
                            <p>{data?.employment?.employer_street_address}</p>
                            <p>{data?.employment?.employer_location}</p>
                            <p>{data?.employment?.employer_country}</p>
                        </>
                    } />
                    {
                        !isMycpg && isEditable && 
                        <ContentSpacer>
                            <button className={buttonClasses.linkButton} type='button' onClick={() => onClick(employmentPath)}>{settings?.editEmploymentLinkText?.replace('{name}', data?.employment?.employer_name)}</button>
                        </ContentSpacer>
                    }
                    { RendereNotAvailableText(settings?.editEmploymentNotAvailableText) }
                    {
                        scheduledPayment &&
                        <>
                            <h3 className='generic-accordion-item-subheader'>{settings?.compensationSectionTitle}</h3>

                            <LabelAndValue fullSpanLabel label={compensationSettings.baseSalaryLabel} value={scheduledPayment.cash_salary} tooltipMessage={compensationSettings.baseSalaryLabelHelperText} />
                            <LabelAndValue fullSpanLabel label={compensationSettings.employerContributionLabel} value={scheduledPayment.er_plan_contributions} tooltipMessage={compensationSettings.employerContributionLabelHelperText} />
                            <LabelAndValue fullSpanLabel label={compensationSettings.cashHousingAllowanceLabel} value={scheduledPayment.housing_cash_allowance} tooltipMessage={compensationSettings.cashHousingAllowanceLabelHelperText} />
                            <h3 className='generic-accordion-item-subheader'>{compensationSettings.housingHeader} </h3>
                            <LabelAndValue label={compensationSettings.providedHousingLabel} value={scheduledPayment.housing_provided?.toLowerCase() === 'housing' ? yesText || 'Yes' : noText || 'No'} />
                            <h3 className='generic-accordion-item-subheader'>{compensationSettings.calculatedTotalsHeader}</h3>
                            <CalculatedTotalsDisplay
                                compensationProps={compensationSettings}
                                calculatedTotals={scheduledPayment}
                            />
                        </>
                    }                    
                    {
                        data?.compensations?.otp?.map(
                            o => <OneTimePaymentView
                                key={`opt-${o?.id}`}
                                payment={o}
                                effectiveDateOneTimeLabel={compensationSettings?.effectiveDateOneTimeLabel}
                                amountLabel={compensationSettings?.amountLabel}
                                assessableLabel={compensationSettings?.assessableLabel}
                            />)
                    }
                    {
                        scheduledPayment && isEditable &&
                        <ul className={buttonClasses.linkButtonList}>
                            <li>
                                <button className={buttonClasses.linkButton} type='button' onClick={() => onClick(compensationPath)}>{settings?.editCompensationLinkText?.replace('{name}', data?.employment?.employer_name)}</button></li>
                            <li><button className={buttonClasses.linkButton} type='button' onClick={() => onClick(compensationHistoryPath)}>{settings?.editCompensationHistoryLinkText?.replace('{name}', data?.employment?.employer_name)}</button></li>
                        </ul>
                    }
                    { scheduledPayment && RendereNotAvailableText(settings?.editCompensationNotAvailableText) }
                </div>
            </AccordionDetails>
        </Accordion>
    );
}

EmploymentCompensationItem.defaultProps = {
	isMycpg: false,  
};